import { NgModule } from "@angular/core";
import { PreloadAllModules, RouterModule, Routes } from "@angular/router";
import { AuthGuard } from "./guards/auth.guard";
import { AuthorizationGuard } from "./guards/authorization.guard";
import { ParkingBanRedirectComponent } from "./pages/assignments/assignment-container/assignment-parking-bans/parking-ban-redirect/parking-ban-redirect.component";
import { TaskRedirectComponent } from "./pages/assignments/assignment-container/assignment-tasks/task-redirect/task-redirect.component";
import { ServiceStatusComponent } from "./pages/service-status/service-status.component";
import { AuthenticationService } from "./services/authentication.service";

export enum RouteType {
    Status,
    About,
    Administration,
    Assignments,
    Configuration,
    Data,
    Devices,
    Documentation,
    History,
    Link,
    Locations,
    Groups,
    Notifications,
    Planning,
    Qr,
    Reports,
    Scenarios,
    Worklist,
}

const routes: Routes = [
    //region Redirects

    // Remain compatible with the old /map/measuringpoints route
    { path: "map/measuringpoint/:locationId/:mpId/:tab", redirectTo: "locations/:locationId/:mpId/:tab" },

    // Remain compatible with the old /map/assignments route
    { path: "map/assignments", redirectTo: "assignments" },
    { path: "map/assignments/:assignmentId/parking-bans", redirectTo: "assignments/:assignmentId/parkingbans" },
    { path: "map/assignments/:assignmentId/work-history", redirectTo: "assignments/:assignmentId/workHistory" },
    { path: "map/assignments/**", redirectTo: "assignments/**" },

    // Remain compatible with old map/parkingban route
    { path: "map/parkingban/:parkingBanId", component: ParkingBanRedirectComponent },
    { path: "map/parkingban/:parkingBanId/:tab", component: ParkingBanRedirectComponent },

    // Remain compatible with old map/task route
    { path: "map/task/:taskId", component: TaskRedirectComponent },
    { path: "map/task/:taskId/:tab", component: TaskRedirectComponent },

    //endregion

    {
        path: "status",
        component: ServiceStatusComponent,
        data: { name: "serviceStatus.title" },
    },
    {
        path: "about",
        loadChildren: () => import("./pages/about/about.routing").then((m) => m.ABOUT_ROUTES),
        data: { name: "about.title" },
    },
    {
        path: "administration",
        loadChildren: () =>
            import("./pages/administration/administration.routing").then((m) => m.ADMINISTRATION_ROUTES),
        canActivate: [AuthGuard, AuthorizationGuard],
        data: {
            name: "administration.title",
            rights: AuthenticationService.getRightsPerRoute(RouteType.Administration),
        },
    },
    {
        path: "assignments",
        loadChildren: () => import("./pages/assignments/assignments.routing").then((m) => m.ASSIGNMENTS_ROUTES),
        canActivate: [AuthGuard, AuthorizationGuard],
        data: { name: "assignments.title", rights: AuthenticationService.getRightsPerRoute(RouteType.Assignments) },
    },
    {
        path: "configuration",
        loadChildren: () => import("./pages/configuration/configuration.routing").then((m) => m.CONFIGURATION_ROUTES),
        canActivate: [AuthGuard, AuthorizationGuard],
        data: { name: "management.title", rights: AuthenticationService.getRightsPerRoute(RouteType.Configuration) },
    },
    {
        path: "data",
        loadChildren: () => import("./pages/data/data.routing").then((m) => m.DATA_ROUTES),
        canActivate: [AuthGuard, AuthorizationGuard],
        data: { name: "reports.title", rights: AuthenticationService.getRightsPerRoute(RouteType.Data) },
    },
    {
        path: "devices",
        loadChildren: () => import("./pages/devices/devices.routing").then((m) => m.DEVICES_ROUTES),
        canActivate: [AuthGuard, AuthorizationGuard],
        data: { name: "devices.title", rights: AuthenticationService.getRightsPerRoute(RouteType.Devices) },
    },
    {
        path: "documentation",
        loadChildren: () => import("./pages/documentation/documentation.routing").then((m) => m.DOCUMENTATION_ROUTES),
        canActivate: [AuthGuard],
        data: { name: "documentation.title" },
    },
    {
        path: "history",
        loadChildren: () => import("./pages/history/history.routing").then((m) => m.HISTORY_ROUTES),
        canActivate: [AuthGuard, AuthorizationGuard],
        data: { name: "history.title", rights: AuthenticationService.getRightsPerRoute(RouteType.History) },
    },
    {
        path: "link",
        loadChildren: () => import("./pages/link/link.routing").then((m) => m.LINK_ROUTES),
        canActivate: [AuthGuard, AuthorizationGuard],
        data: { name: "manageLink.title", rights: AuthenticationService.getRightsPerRoute(RouteType.Link) },
    },
    {
        path: "locations",
        loadChildren: () => import("./pages/installations/installations.routing").then((m) => m.INSTALLATION_ROUTES),
        canActivate: [AuthGuard, AuthorizationGuard],
        data: { name: "locations.title", rights: AuthenticationService.getRightsPerRoute(RouteType.Locations) },
    },
    {
        path: "notifications",
        loadChildren: () => import("./pages/notifications/notifications.routing").then((m) => m.NOTIFICATIONS_ROUTES),
        canActivate: [AuthGuard],
        data: { name: "notifications.title" },
    },
    {
        path: "planning",
        loadChildren: () =>
            import("./pages/planning/assignment-planning.routing").then((m) => m.ASSIGNMENTS_PLANNING_ROUTES),
        canActivate: [AuthGuard, AuthorizationGuard],
        data: { name: "assignmentPlanning.title", rights: AuthenticationService.getRightsPerRoute(RouteType.Planning) },
    },
    {
        path: "reports",
        loadChildren: () => import("./pages/reports/reporting.routing").then((m) => m.REPORTING_ROUTES),
        canActivate: [AuthGuard, AuthorizationGuard],
        data: { name: "reports.title", rights: AuthenticationService.getRightsPerRoute(RouteType.Reports) },
    },
    {
        path: "worklists",
        loadChildren: () => import("./pages/worklists/worklists.routing").then((m) => m.WORKLISTS_ROUTES),
        canActivate: [AuthGuard, AuthorizationGuard],
        data: { name: "worklists.title", rights: AuthenticationService.getRightsPerRoute(RouteType.Worklist) },
    },
    {
        path: "**",
        redirectTo: "locations",
    },
];

// onSameUrlNavigation is set to reload, this is to force angular to always reload page even when navigating to the same page
// This has been done due to an issue when navigating away from locations page the title would not update (NavigationEnd was never reached, due to same page navigation (which is not true, but can not find root cause))
@NgModule({
    imports: [RouterModule.forRoot(routes, { preloadingStrategy: PreloadAllModules, onSameUrlNavigation: "reload" })],
    exports: [RouterModule],
})
export class AppRoutingModule {}
